import { LocalNotifications, type Channel } from '@capacitor/local-notifications';
import { logger } from '../lib/logger.js';
import { isNativePlatform } from './device.js';

/**
 * Las tres notificaciones del §8 de UI_UX_SPEC.md.
 *
 * Como el resto de `platform/`, todo degrada sin romper nada: si el navegador
 * no tiene la API, o el usuario denegó el permiso, la aplicación sigue
 * funcionando exactamente igual. Un aviso que no llega es una molestia; una
 * pantalla que revienta por no poder avisar es un fallo.
 *
 * QUÉ SE PUEDE Y QUÉ NO, POR PLATAFORMA
 *
 * Es la diferencia que R-02 llevaba desde agosto sin poder cerrar, y conviene
 * escribirla donde vive el código y no solo en un documento:
 *
 * * **Android** (Capacitor, `@capacitor/local-notifications`): se pueden
 *   PROGRAMAR avisos para más tarde, y llegan con la aplicación cerrada.
 * * **Windows** (PWA, `Notification` del navegador): **no hay programación**.
 *   No existe forma de pedir un aviso futuro sin un servicio de push, que este
 *   proyecto no tiene ni va a tener por un recordatorio de entrenamiento. Aquí
 *   un aviso solo puede dispararse mientras la aplicación está abierta, y por
 *   eso el §8 marca el recordatorio como «silenciosa en Windows».
 *
 * No se finge la diferencia: `canSchedule()` la dice, y quien programa decide
 * qué hacer con la respuesta.
 */

/** Identificadores estables: reprogramar sustituye, no acumula. */
export const NOTIFICACION = {
  recordatorio: 1,
  descanso: 2,
  checkin: 3,
} as const;

export type NotificacionId = (typeof NOTIFICACION)[keyof typeof NOTIFICACION];

/**
 * LOS CANALES, Y POR QUE HAY QUE DECLARARLOS
 *
 * En Android 8 y posteriores el sonido, la vibracion y la insistencia de un
 * aviso NO son del aviso: son del CANAL por el que sale. El plugin trae un
 * canal `default` con importancia media y —esto es lo que se nos escapo— sin
 * llamar a `enableVibration(true)`. Un `NotificationChannel` recien construido
 * tiene la vibracion APAGADA, asi que el aviso de fin de descanso llegaba mudo
 * de vibracion aunque el movil estuviera en la mano: no fallaba nada, es que
 * nadie habia pedido que vibrase.
 *
 * Los ajustes de un canal son INMUTABLES una vez creado —solo el usuario puede
 * cambiarlos desde los ajustes del sistema— asi que el identificador lleva
 * version: cambiar la configuracion obliga a cambiar el id, o el sistema
 * ignorara la nueva en cualquier movil donde la aplicacion ya se instalo.
 */
export const CANAL = {
  /** Alta importancia, vibracion y sonido: el descanso se acaba mientras no miras. */
  descanso: 'descanso-v1',
  /** Importancia normal, pero con vibracion: sin ella tambien llegaba mudo. */
  avisos: 'avisos-v1',
} as const;

export type CanalId = (typeof CANAL)[keyof typeof CANAL];

const CANALES: readonly Channel[] = [
  {
    id: CANAL.descanso,
    name: 'Fin del descanso',
    description: 'Avisa cuando se agota el temporizador entre series.',
    // 5 = MAX: asoma sobre lo que haya en pantalla. Es un aviso que caduca en
    // segundos; llegar callado a la lista de notificaciones no sirve de nada.
    importance: 5,
    vibration: true,
    visibility: 1,
  },
  {
    id: CANAL.avisos,
    name: 'Recordatorios',
    description: 'Entrenamiento del dia y check-in semanal.',
    importance: 4,
    vibration: true,
    visibility: 1,
  },
];

let canalesCreados = false;

/**
 * Crea los canales, una vez por arranque.
 *
 * Repetirlo es inofensivo —`createNotificationChannel` con un id existente solo
 * refresca el nombre y la descripcion— pero no hace falta pagarlo en cada
 * aviso. En Windows no existe el concepto y `createChannel` responde
 * `unavailable`, asi que ni se intenta.
 */
async function asegurarCanales(): Promise<void> {
  if (canalesCreados || !isNativePlatform()) return;
  try {
    for (const canal of CANALES) await LocalNotifications.createChannel(canal);
    canalesCreados = true;
  } catch (error) {
    logger.debug('ui', 'NOTIF_CHANNEL_UNAVAILABLE', 'No se pudieron crear los canales', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

export interface Aviso {
  id: NotificacionId;
  titulo: string;
  cuerpo: string;
  /** Canal de Android. Decide vibracion, sonido e insistencia. */
  canal?: CanalId | undefined;
  /**
   * Si el aviso debe sonar en Windows. Por omision NO: el §8 marca el
   * recordatorio como silencioso y un sonido del sistema en mitad de una serie
   * sobresalta mas de lo que ayuda. El fin del descanso es la excepcion, porque
   * ahi el sonido ES el aviso.
   */
  sonoro?: boolean | undefined;
}

/** ¿Se pueden programar avisos para más tarde, con la aplicación cerrada? */
export function canSchedule(): boolean {
  return isNativePlatform();
}

/**
 * Pide permiso, si hace falta.
 *
 * Devuelve si se puede avisar. NO lanza: que el usuario diga que no es una
 * respuesta legítima, no un error.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  try {
    if (isNativePlatform()) {
      const actual = await LocalNotifications.checkPermissions();
      if (actual.display === 'granted') return true;
      const pedido = await LocalNotifications.requestPermissions();
      return pedido.display === 'granted';
    }

    const Notificacion = (globalThis as { Notification?: typeof Notification }).Notification;
    if (Notificacion === undefined) return false;
    if (Notificacion.permission === 'granted') return true;
    if (Notificacion.permission === 'denied') return false;
    return (await Notificacion.requestPermission()) === 'granted';
  } catch (error) {
    logger.debug('ui', 'NOTIF_PERMISSION_UNAVAILABLE', 'No se pudo pedir permiso de avisos', {
      message: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

/** ¿Está ya concedido? Sin preguntar nada al usuario. */
export async function hasNotificationPermission(): Promise<boolean> {
  try {
    if (isNativePlatform()) {
      return (await LocalNotifications.checkPermissions()).display === 'granted';
    }
    const Notificacion = (globalThis as { Notification?: typeof Notification }).Notification;
    return Notificacion?.permission === 'granted';
  } catch {
    return false;
  }
}

/** Avisa AHORA. Es lo único que funciona en las dos plataformas. */
export async function showNotification(aviso: Aviso): Promise<boolean> {
  try {
    if (!(await hasNotificationPermission())) return false;

    if (isNativePlatform()) {
      await asegurarCanales();
      await LocalNotifications.schedule({
        notifications: [
          {
            id: aviso.id,
            title: aviso.titulo,
            body: aviso.cuerpo,
            channelId: aviso.canal ?? CANAL.avisos,
          },
        ],
      });
      return true;
    }

    const Notificacion = (globalThis as { Notification?: typeof Notification }).Notification;
    if (Notificacion === undefined) return false;
    // Silencioso por omisión: el §8 marca el recordatorio como silencioso en
    // Windows, y un sonido del sistema en mitad de una serie sobresalta más de
    // lo que ayuda. El fin del descanso pide sonar, y ahí sí suena.
    new Notificacion(aviso.titulo, {
      body: aviso.cuerpo,
      silent: aviso.sonoro !== true,
      tag: String(aviso.id),
    });
    return true;
  } catch (error) {
    logger.debug('ui', 'NOTIF_UNAVAILABLE', 'No se pudo mostrar el aviso', {
      message: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

/**
 * Programa un aviso para un instante futuro. Solo Android.
 *
 * Devuelve `false` en Windows en vez de disimular: la PWA no puede programar
 * nada sin un servicio de push. Quien llama decide —hoy, avisar en el momento
 * si la aplicación está abierta— y así la limitación se ve en el código en
 * lugar de aparecer como un aviso que nunca llega.
 */
export async function scheduleNotification(aviso: Aviso, cuando: Date): Promise<boolean> {
  try {
    if (!canSchedule()) return false;
    if (!(await hasNotificationPermission())) return false;

    await asegurarCanales();
    await LocalNotifications.schedule({
      notifications: [
        {
          id: aviso.id,
          title: aviso.titulo,
          body: aviso.cuerpo,
          channelId: aviso.canal ?? CANAL.avisos,
          // `allowWhileIdle` ES LO QUE HACE QUE SUENE CON LA PANTALLA APAGADA.
          //
          // Sin él, el plugin programa la alarma con `AlarmManager.RTC`, que
          // NO despierta el dispositivo: el aviso espera a que alguien encienda
          // la pantalla. Para un descanso de noventa segundos eso es no avisar.
          // Con él pasa a `RTC_WAKEUP` —exacto si el sistema deja, y si no
          // aproximado, pero despertando— que es la diferencia entre un
          // temporizador y un adorno.
          schedule: { at: cuando, allowWhileIdle: true },
        },
      ],
    });
    return true;
  } catch (error) {
    logger.debug('ui', 'NOTIF_SCHEDULE_UNAVAILABLE', 'No se pudo programar el aviso', {
      message: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

/**
 * Cancela lo programado.
 *
 * Hace falta al apagar el interruptor: sin esto, apagar los avisos dejaría en
 * la cola del sistema los que ya estuvieran puestos, y seguirían llegando
 * después de haberlos desactivado —que es exactamente lo que hace que alguien
 * desinstale la aplicación—.
 */
export async function cancelScheduled(ids: readonly NotificacionId[]): Promise<void> {
  try {
    if (!canSchedule()) return;
    await LocalNotifications.cancel({ notifications: ids.map((id) => ({ id })) });
  } catch (error) {
    logger.debug('ui', 'NOTIF_CANCEL_UNAVAILABLE', 'No se pudieron cancelar los avisos', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * ESTADO DEL PERMISO DE «ALARMAS Y RECORDATORIOS» (Android 12+).
 *
 * Es un permiso aparte del de notificaciones y no se pide con un dialogo: hay
 * que mandar al usuario a una pantalla de ajustes del sistema. Sin él, Android
 * degrada la alarma del descanso a INEXACTA y puede entregarla varios minutos
 * tarde —sigue despertando el movil, porque `allowWhileIdle` lo garantiza, pero
 * a su hora ya no—. Para un temporizador entre series eso lo inutiliza.
 *
 * `no-aplica` es Windows y las versiones de Android donde no existe el permiso.
 * No es un fallo ni hay nada que ofrecer al usuario.
 */
export type AlarmasExactas = 'concedido' | 'denegado' | 'no-aplica';

export async function estadoAlarmasExactas(): Promise<AlarmasExactas> {
  try {
    if (!isNativePlatform()) return 'no-aplica';
    const estado = await LocalNotifications.checkExactNotificationSetting();
    return estado.exact_alarm === 'granted' ? 'concedido' : 'denegado';
  } catch {
    // El metodo no existe antes de Android 12: ahi las alarmas ya son exactas.
    return 'no-aplica';
  }
}

/**
 * Abre los ajustes del sistema para concederlo.
 *
 * Conceder o revocar este permiso REINICIA el proceso de la aplicación y borra
 * lo que hubiera programado, así que solo se llama desde la pantalla de Avisos
 * y nunca en mitad de un entrenamiento.
 */
export async function pedirAlarmasExactas(): Promise<AlarmasExactas> {
  try {
    if (!isNativePlatform()) return 'no-aplica';
    const estado = await LocalNotifications.changeExactNotificationSetting();
    return estado.exact_alarm === 'granted' ? 'concedido' : 'denegado';
  } catch (error) {
    logger.debug('ui', 'NOTIF_EXACT_UNAVAILABLE', 'No se pudo abrir el ajuste de alarmas', {
      message: error instanceof Error ? error.message : String(error),
    });
    return 'no-aplica';
  }
}
